import React, { useState } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { LOJ_COLORS } from '../theme/lojTheme';
import { CrystalClashStrategy, UnitBlock, SquadRole } from '../data/strategies';
import OrnamentalDivider from './OrnamentalDivider';
import CrystalClashRoster from './CrystalClashRoster';
import { useTranslation } from '../i18n/LanguageContext';

// ─── Accent color map ─────────────────────────────────────────────────────────

const ACCENT: Record<string, { border: string; glow: string; badge: string }> = {
  blue:  { border: LOJ_COLORS.crystalLight, glow: LOJ_COLORS.crystalGlow,  badge: LOJ_COLORS.crystalMid  },
  amber: { border: LOJ_COLORS.sandLight,    glow: LOJ_COLORS.sandGlow,      badge: LOJ_COLORS.sandMid     },
  red:   { border: LOJ_COLORS.ember,        glow: LOJ_COLORS.blood,         badge: LOJ_COLORS.bloodDark   },
  gold:  { border: LOJ_COLORS.goldBright,   glow: LOJ_COLORS.goldGlow,      badge: LOJ_COLORS.goldDim     },
};

// ─── Role pill ────────────────────────────────────────────────────────────────

const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  warrior:  { bg: alpha(LOJ_COLORS.bloodDark, 0.6),   text: LOJ_COLORS.ember        },
  engineer: { bg: alpha(LOJ_COLORS.crystalDeep, 0.8), text: LOJ_COLORS.crystalLight },
  all:      { bg: alpha(LOJ_COLORS.goldDim, 0.4),     text: LOJ_COLORS.goldBright   },
};

const RolePill: React.FC<{ role: SquadRole }> = ({ role }) => {
  const { t } = useTranslation();
  const c = ROLE_COLORS[role.role] ?? ROLE_COLORS.all;
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        background: alpha(LOJ_COLORS.void, 0.5),
        border: `1px solid ${alpha(c.text, 0.3)}`,
        borderLeft: `3px solid ${c.text}`,
      }}
    >
      <Box
        sx={{
          display: 'inline-block',
          px: 1.5,
          py: 0.25,
          mb: 1.5,
          background: c.bg,
          border: `1px solid ${alpha(c.text, 0.5)}`,
          fontFamily: "'Cinzel', serif",
          fontSize: '0.7rem',
          letterSpacing: '0.12em',
          color: c.text,
          fontWeight: 700,
        }}
      >
        {t(role.label).toUpperCase()}
      </Box>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {role.instructions.map((line, i) => (
          <Box
            component="li"
            key={i}
            sx={{
              mb: 0.8,
              color: LOJ_COLORS.parchment,
              fontSize: '1rem',
              lineHeight: 1.65,
              fontFamily: "'Crimson Text', Georgia, serif",
              '&::marker': { color: alpha(c.text, 0.7) },
            }}
          >
            {t(line)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Unit Block Card ──────────────────────────────────────────────────────────

const UnitCard: React.FC<{ unit: UnitBlock; index: number }> = ({ unit, index }) => {
  const { t } = useTranslation();
  const key = unit.accentKey ?? 'blue';
  const ac = ACCENT[key];

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 3,
        p: { xs: 2.5, md: 3.5 },
        background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.95)}, ${alpha(LOJ_COLORS.stone, 0.85)})`,
        border: `1px solid ${alpha(ac.border, 0.3)}`,
        borderLeft: `3px solid ${ac.border}`,
        boxShadow: `
          0 4px 24px ${alpha(LOJ_COLORS.void, 0.8)},
          inset 0 1px 0 ${alpha(ac.border, 0.15)},
          -4px 0 20px ${alpha(ac.border, 0.08)}
        `,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(to right, ${ac.border}, transparent)`,
          opacity: 0.5,
        },
        transition: 'box-shadow 0.3s ease, transform 0.2s ease',
        '&:hover': {
          transform: 'translateX(2px)',
          boxShadow: `
            0 6px 32px ${alpha(LOJ_COLORS.void, 0.9)},
            inset 0 1px 0 ${alpha(ac.border, 0.25)},
            -4px 0 28px ${alpha(ac.border, 0.15)}
          `,
        },
      }}
    >
      {/* Index badge */}
      <Box
        sx={{
          position: 'absolute',
          top: -1,
          right: 20,
          px: 2,
          py: 0.4,
          background: `linear-gradient(135deg, ${alpha(ac.border, 0.9)}, ${alpha(ac.border, 0.55)})`,
          border: `1px solid ${alpha(ac.border, 0.5)}`,
          borderTop: 'none',
          fontSize: '0.62rem',
          fontFamily: "'Cinzel', serif",
          letterSpacing: '0.15em',
          color: LOJ_COLORS.abyss,
          fontWeight: 700,
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontSize: '1.4rem', lineHeight: 1 }}>{unit.icon}</Typography>
        <Typography
          variant="h5"
          sx={{
            fontFamily: "'Cinzel', serif",
            fontWeight: 600,
            fontSize: { xs: '0.95rem', md: '1.05rem' },
            color: ac.border,
            letterSpacing: '0.04em',
            textShadow: `0 0 18px ${alpha(ac.glow, 0.35)}`,
          }}
        >
          {t(unit.label)}
        </Typography>
      </Box>

      {/* Instructions */}
      <Box component="ul" sx={{ m: 0, pl: 2.5, mb: unit.roles ? 1 : 0 }}>
        {unit.instructions.map((line, i) => (
          <Box
            component="li"
            key={i}
            sx={{
              mb: 1,
              color: LOJ_COLORS.parchmentLight,
              fontSize: '1.05rem',
              lineHeight: 1.7,
              fontFamily: "'Crimson Text', Georgia, serif",
              '&::marker': { color: alpha(ac.border, 0.7) },
            }}
          >
            {t(line)}
          </Box>
        ))}
      </Box>

      {/* Role blocks */}
      {unit.roles?.map((role, i) => (
        <RolePill key={i} role={role} />
      ))}

      {/* Note */}
      {unit.note && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 1.5,
            background: alpha(LOJ_COLORS.void, 0.35),
            border: `1px solid ${alpha(LOJ_COLORS.goldDim, 0.3)}`,
          }}
        >
          <Typography sx={{ color: LOJ_COLORS.goldBright, fontSize: '0.9rem', mt: '2px', flexShrink: 0 }}>◆</Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: LOJ_COLORS.parchment, fontSize: '0.98rem' }}>
            {t(unit.note)}
          </Typography>
        </Box>
      )}

      {/* Warning */}
      {unit.warning && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.bloodDark, 0.7)}, ${alpha(LOJ_COLORS.stone, 0.5)})`,
            border: `1px solid ${alpha(LOJ_COLORS.ember, 0.4)}`,
            borderLeft: `3px solid ${LOJ_COLORS.ember}`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontSize: '1rem', flexShrink: 0, color: LOJ_COLORS.ember }}>⚠</Typography>
          <Typography
            variant="body2"
            sx={{ color: alpha(LOJ_COLORS.ember, 0.9), fontSize: '0.98rem', fontFamily: "'Crimson Text', Georgia, serif", fontWeight: 600 }}
          >
            {t(unit.warning)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Plan toggle tabs ─────────────────────────────────────────────────────────

const PLAN_COLORS = [
  { border: LOJ_COLORS.crystalLight, glow: LOJ_COLORS.crystalGlow },
  { border: LOJ_COLORS.goldBright,   glow: LOJ_COLORS.goldGlow      },
  { border: LOJ_COLORS.ember,        glow: LOJ_COLORS.blood        },
];

// ─── Main Panel ───────────────────────────────────────────────────────────────

interface CrystalClashPanelProps {
  strategy: CrystalClashStrategy;
  hidden: boolean;
}

const CrystalClashPanel: React.FC<CrystalClashPanelProps> = ({ strategy, hidden }) => {
  const { t } = useTranslation();
  const [activePlan, setActivePlan] = useState(0);
  const plan = strategy.plans[activePlan];
  const pc = PLAN_COLORS[activePlan] ?? PLAN_COLORS[0];

  return (
    <Box
      role="tabpanel"
      hidden={hidden}
      sx={{
        display: hidden ? 'none' : 'block',
        animation: hidden ? 'none' : 'fadeIn 0.4s ease',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to:   { opacity: 1, transform: 'translateY(0)'    },
        },
      }}
    >
      {/* Event Header */}
      <Box
        sx={{
          position: 'relative',
          mb: 4,
          pt: 5,
          pb: 4,
          px: { xs: 3, md: 5 },
          background: `linear-gradient(180deg, ${alpha(LOJ_COLORS.void, 0.4)} 0%, ${alpha(LOJ_COLORS.crystalLight, 0.04)} 50%, transparent 100%)`,
          borderBottom: `1px solid ${alpha(LOJ_COLORS.crystalLight, 0.2)}`,
          textAlign: 'center',
        }}
      >
        {/* Corner ornaments */}
        {['topleft', 'topright'].map((pos) => (
          <Box
            key={pos}
            sx={{
              position: 'absolute',
              top: 12,
              ...(pos === 'topleft' ? { left: 12 } : { right: 12 }),
              width: 40,
              height: 40,
              borderTop: `2px solid ${alpha(LOJ_COLORS.crystalLight, 0.5)}`,
              ...(pos === 'topleft'
                ? { borderLeft: `2px solid ${alpha(LOJ_COLORS.crystalLight, 0.5)}` }
                : { borderRight: `2px solid ${alpha(LOJ_COLORS.crystalLight, 0.5)}` }),
            }}
          />
        ))}

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '2rem', md: '2.8rem', lg: '3.2rem' },
            color: LOJ_COLORS.crystalLight,
            textShadow: `
              0 0 20px ${alpha(LOJ_COLORS.crystalGlow, 0.6)},
              0 0 40px ${alpha(LOJ_COLORS.crystalGlow, 0.3)},
              0 2px 4px ${alpha(LOJ_COLORS.void, 0.9)}
            `,
            mb: 1,
          }}
        >
          {strategy.eventName}
        </Typography>

        <Typography variant="subtitle1" sx={{ color: LOJ_COLORS.parchment, letterSpacing: '0.2em', fontSize: '0.8rem', opacity: 0.8 }}>
          — {t(strategy.subtitle)} —
        </Typography>
      </Box>

      {/* Plan selector */}
      <Box sx={{ px: { xs: 2, md: 4 }, mb: 1 }}>
        <OrnamentalDivider label={t('Select Formation')} color={LOJ_COLORS.crystalLight} />

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap',
          }}
        >
          {strategy.plans.map((p, i) => {
            const active = activePlan === i;
            const c = PLAN_COLORS[i] ?? PLAN_COLORS[0];
            return (
              <Box
                key={p.name}
                onClick={() => setActivePlan(i)}
                sx={{
                  flex: '1 1 200px',
                  cursor: 'pointer',
                  p: 2.5,
                  background: active
                    ? `linear-gradient(135deg, ${alpha(c.border, 0.18)}, ${alpha(c.border, 0.08)})`
                    : `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.9)}, ${alpha(LOJ_COLORS.stone, 0.7)})`,
                  border: `1px solid ${active ? alpha(c.border, 0.7) : alpha(c.border, 0.2)}`,
                  borderTop: active ? `3px solid ${c.border}` : `1px solid ${alpha(c.border, 0.2)}`,
                  boxShadow: active ? `0 0 24px ${alpha(c.glow, 0.2)}, inset 0 0 20px ${alpha(c.border, 0.05)}` : 'none',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: alpha(c.border, active ? 0.7 : 0.45),
                    background: `linear-gradient(135deg, ${alpha(c.border, active ? 0.18 : 0.1)}, ${alpha(c.border, 0.05)})`,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Cinzel', serif",
                    fontWeight: 700,
                    fontSize: '1rem',
                    letterSpacing: '0.1em',
                    color: active ? c.border : LOJ_COLORS.parchment,
                    textShadow: active ? `0 0 12px ${alpha(c.glow, 0.5)}` : 'none',
                    mb: 0.5,
                  }}
                >
                  {p.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Crimson Text', Georgia, serif",
                    fontSize: '0.9rem',
                    color: active ? alpha(c.border, 0.8) : LOJ_COLORS.parchmentDark,
                    fontStyle: 'italic',
                  }}
                >
                  {t(p.tagline)}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Plan overview */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            mb: plan.mapImage ? 3 : 4,
            background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.8)}, ${alpha(LOJ_COLORS.stone, 0.6)})`,
            border: `1px solid ${alpha(pc.border, 0.2)}`,
            position: 'relative',
          }}
        >
          <Typography
            sx={{
              position: 'absolute',
              top: -12,
              left: 20,
              fontSize: '3rem',
              lineHeight: 1,
              color: alpha(pc.border, 0.25),
              fontFamily: 'Georgia, serif',
            }}
          >
            "
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontStyle: 'italic', color: LOJ_COLORS.parchment, fontSize: '1.1rem', lineHeight: 1.8 }}
          >
            {t(plan.overview)}
          </Typography>
        </Box>

        {/* Map image */}
        {plan.mapImage && (
          <Box
            sx={{
              mb: 4,
              position: 'relative',
              border: `1px solid ${alpha(pc.border, 0.35)}`,
              boxShadow: `
                0 0 0 1px ${alpha(LOJ_COLORS.void, 0.8)},
                0 8px 32px ${alpha(LOJ_COLORS.void, 0.9)},
                0 0 40px ${alpha(pc.glow, 0.12)}
              `,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(180deg, transparent 70%, ${alpha(LOJ_COLORS.abyss, 0.4)} 100%)`,
                pointerEvents: 'none',
                zIndex: 1,
              },
            }}
          >
            {/* Corner ornaments */}
            {[
              { top: 0, left: 0,  borderTop: true,  borderLeft: true  },
              { top: 0, right: 0, borderTop: true,  borderRight: true },
              { bottom: 0, left: 0,  borderBottom: true, borderLeft: true  },
              { bottom: 0, right: 0, borderBottom: true, borderRight: true },
            ].map((pos, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  zIndex: 2,
                  width: 20,
                  height: 20,
                  ...pos,
                  borderTop:    pos.borderTop    ? `2px solid ${alpha(pc.border, 0.7)}` : 'none',
                  borderBottom: pos.borderBottom ? `2px solid ${alpha(pc.border, 0.7)}` : 'none',
                  borderLeft:   pos.borderLeft   ? `2px solid ${alpha(pc.border, 0.7)}` : 'none',
                  borderRight:  pos.borderRight  ? `2px solid ${alpha(pc.border, 0.7)}` : 'none',
                  pointerEvents: 'none',
                }}
              />
            ))}

            <Box
              component="img"
              src={plan.mapImage}
              alt={`${plan.name} map`}
              sx={{
                display: 'block',
                width: '100%',
                maxWidth: 680,
                mx: 'auto',
                height: 'auto',
                filter: 'brightness(0.92) contrast(1.05) saturate(1.1)',
              }}
            />

            {/* Map label */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 2,
                display: 'flex',
                justifyContent: 'center',
                pb: 1,
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 0.4,
                  background: alpha(LOJ_COLORS.void, 0.85),
                  border: `1px solid ${alpha(pc.border, 0.4)}`,
                  fontFamily: "'Cinzel', serif",
                  fontSize: '0.65rem',
                  letterSpacing: '0.2em',
                  color: pc.border,
                }}
              >
                ✦ {plan.name.toUpperCase()} — {t('ZONE MAP')} ✦
              </Box>
            </Box>
          </Box>
        )}

        {/* Unit blocks */}
        <OrnamentalDivider label={`${plan.name} — ${t('Unit Orders')}`} color={pc.border} />

        <Box
          sx={{
            animation: 'planFade 0.3s ease',
            '@keyframes planFade': {
              from: { opacity: 0, transform: 'translateY(6px)' },
              to:   { opacity: 1, transform: 'translateY(0)'   },
            },
          }}
        >
          {plan.units.map((unit, i) => (
            <UnitCard key={i} unit={unit} index={i} />
          ))}
        </Box>

        {/* Commander's Notes */}
        <OrnamentalDivider label={t("Commander's Notes")} color={LOJ_COLORS.goldBright} />

        <Box
          sx={{
            p: { xs: 2.5, md: 4 },
            background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.void, 0.8)}, ${alpha(LOJ_COLORS.darkStone, 0.9)})`,
            border: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.25)}`,
            position: 'relative',
            mb: 4,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 4,
              border: `1px solid ${alpha(LOJ_COLORS.goldDim, 0.2)}`,
              pointerEvents: 'none',
            },
          }}
        >
          <Typography
            variant="h4"
            sx={{ mb: 3, fontSize: '1.1rem', color: LOJ_COLORS.goldBright, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <span>⚜</span> {t("Commander's Notes")}
          </Typography>

          <Box component="ol" sx={{ m: 0, pl: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {strategy.commandersNotes.map((note, i) => (
              <Box
                component="li"
                key={i}
                sx={{
                  color: LOJ_COLORS.parchmentLight,
                  fontSize: '1.05rem',
                  lineHeight: 1.7,
                  fontFamily: "'Crimson Text', Georgia, serif",
                  pl: 0.5,
                  '&::marker': { color: LOJ_COLORS.goldBright, fontFamily: "'Cinzel', serif", fontWeight: 600 },
                }}
              >
                {t(note)}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Weekly Roster */}
        <CrystalClashRoster />

      </Box>
    </Box>
  );
};

export default CrystalClashPanel;
